import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Grid2x2, LayoutList, LogOut, Menu as MenuIcon, Moon, Search, Sun, Monitor, Upload, Keyboard, Type, FolderUp } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useSessionStore } from '@/stores/session';
import { useUiStore } from '@/stores/ui';
import { useGridPrefs } from '@/stores/gridPrefs';
import { usePresence, type Viewer } from '@/stores/presence';
import { useShallow } from 'zustand/react/shallow';

const EMPTY_VIEWERS: Viewer[] = [];
import { CARD_WIDTH_MAX, CARD_WIDTH_MIN } from '@/config/constants';
import { IconButton, Button } from '@/components/Button';
import { Avatar } from '@/components/Misc';
import { Menu, type MenuItem } from '@/components/Menu';
import { Select } from '@/components/Input';
import { isEditableTarget } from '@/lib/keyboard';
import { cx, isMac } from '@/lib/format';
import { Breadcrumbs } from './Breadcrumbs';
import { usePermission } from './usePermission';
import { uploadManager } from '@/features/upload/UploadManager';
import { useBoard } from '@/lib/api/queries';
import type { BoardAssetSort } from '@/lib/api/types';
// --- release3/A ---
import { NotificationBell } from '@/features/notifications/NotificationBell';

const SORTS: Array<{ v: BoardAssetSort; label: string }> = [
  { v: 'added_at:desc', label: 'Newest added' },
  { v: 'added_at:asc', label: 'Oldest added' },
  { v: 'name:asc', label: 'Name A–Z' },
  { v: 'name:desc', label: 'Name Z–A' },
  { v: 'size:desc', label: 'Largest' },
  { v: 'size:asc', label: 'Smallest' },
  { v: 'created_at:desc', label: 'Newest created' },
  { v: 'sort_order:asc', label: 'Manual order' },
];

export function TopBar() {
  const { ws = '', boardId } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();
  const me = useSessionStore((s) => s.me);
  const setDrawer = useUiStore((s) => s.setSidebarDrawer);
  const prefs = useGridPrefs();
  const searchRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState(params.get('q') ?? '');
  const canUpload = usePermission('UPLOAD', { boardId });
  const fileInput = useRef<HTMLInputElement>(null);
  const dirInput = useRef<HTMLInputElement>(null);
  const [userMenu, setUserMenu] = useState(false);
  const [uploadMenu, setUploadMenu] = useState(false);
  const userBtn = useRef<HTMLButtonElement>(null);
  const uploadBtn = useRef<HTMLButtonElement>(null);
  const viewers = usePresence(useShallow((s) => (boardId ? (s.rooms[`board:${boardId}`] ?? EMPTY_VIEWERS) : EMPTY_VIEWERS).filter((v) => v.connection_id !== 'self')));
  const qc = useQueryClient();
  const onBoard = !!boardId;
  const smartBoard = useBoard(boardId).data?.kind === 'smart'; // release2/A: a live query takes no uploads (409 board_is_smart)
  const isSearch = loc.pathname.endsWith('/search');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !isEditableTarget(e) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (isSearch) setQ(params.get('q') ?? '');
  }, [isSearch, params]);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    navigate(`/w/${ws}/search?q=${encodeURIComponent(q)}&mode=hybrid`);
  }

  const sort = (params.get('sort') as BoardAssetSort | null) ?? (boardId ? prefs.sortByBoard[boardId] : undefined) ?? 'added_at:desc';

  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const setShortcuts = useUiStore((s) => s.setShortcutsOpen);
  const userItems: MenuItem[] = [
    { kind: 'label', label: me?.user.email ?? '' },
    { label: 'Light theme', icon: <Sun size={14} />, onSelect: () => setTheme('light'), title: theme === 'light' ? 'current' : undefined },
    { label: 'Dark theme', icon: <Moon size={14} />, onSelect: () => setTheme('dark') },
    { label: 'System theme', icon: <Monitor size={14} />, onSelect: () => setTheme('system') },
    { kind: 'separator' },
    { label: 'Keyboard shortcuts', icon: <Keyboard size={14} />, shortcut: '?', onSelect: () => setShortcuts(true) },
    { kind: 'separator' },
    {
      label: 'Sign out',
      icon: <LogOut size={14} />,
      onSelect: async () => {
        await api.POST('/auth/logout');
        qc.clear();
        useSessionStore.getState().setMe(null);
        navigate('/login');
      },
    },
  ];
  const uploadItems: MenuItem[] = [
    { label: 'Upload files', icon: <Upload size={14} />, onSelect: () => fileInput.current?.click() },
    { label: 'Upload folder', icon: <FolderUp size={14} />, onSelect: () => dirInput.current?.click() },
  ];

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-bg-elev px-2 md:px-3">
      <IconButton label="Open navigation" className="md:hidden" onClick={() => setDrawer(true)}>
        <MenuIcon size={18} />
      </IconButton>
      <div className="min-w-0 flex-1">
        <Breadcrumbs />
      </div>
      <form onSubmit={submitSearch} role="search" className="relative hidden w-56 sm:block lg:w-72">
        <Search size={14} className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-fg-subtle" />
        <input
          ref={searchRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…  /"
          aria-label="Search assets"
          className="h-8 w-full rounded-md border border-line bg-bg pl-7 pr-2 text-sm outline-none focus:border-accent"
        />
      </form>
      {onBoard && (
        <>
          <Select aria-label="Sort" value={sort} onChange={(e) => {
            const v = e.target.value as BoardAssetSort;
            if (boardId) prefs.setSort(boardId, v);
            params.set('sort', v);
            setParams(params, { replace: true });
          }} className="hidden h-8 w-36 lg:block">
            {SORTS.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </Select>
          <input
            type="range"
            min={CARD_WIDTH_MIN}
            max={CARD_WIDTH_MAX}
            step={8}
            value={prefs.targetCardWidth}
            onChange={(e) => prefs.setTargetCardWidth(Number(e.target.value))}
            aria-label="Card size"
            title={`Card size ${prefs.targetCardWidth}px`}
            className="hidden w-24 lg:block"
          />
          <IconButton label={prefs.showTitles ? 'Hide titles' : 'Show titles'} active={prefs.showTitles} onClick={() => prefs.setShowTitles(!prefs.showTitles)}>
            <Type size={16} />
          </IconButton>
          <IconButton label={prefs.view === 'grid' ? 'Switch to list view' : 'Switch to grid view'} onClick={() => prefs.setView(prefs.view === 'grid' ? 'list' : 'grid')}>
            {prefs.view === 'grid' ? <LayoutList size={16} /> : <Grid2x2 size={16} />}
          </IconButton>
        </>
      )}
      {viewers.length > 0 && (
        <div className="hidden items-center -space-x-1.5 md:flex" aria-label={`${viewers.length} viewing`} title={viewers.map((v) => v.actor.display_name ?? v.actor.guest_name ?? 'Guest').join(', ')}>
          {viewers.slice(0, 4).map((v) => (
            <Avatar key={v.connection_id} actor={v.actor} size={22} className="ring-2 ring-bg-elev" />
          ))}
        </div>
      )}
      {canUpload && onBoard && !smartBoard && (
        <>
          <Button ref={uploadBtn} variant="primary" size="sm" onClick={() => setUploadMenu(true)} aria-haspopup="menu" aria-expanded={uploadMenu}>
            <Upload size={14} /> <span className="hidden sm:inline">Upload</span>
          </Button>
          {uploadMenu && <Menu anchor={uploadBtn.current} items={uploadItems} onClose={() => setUploadMenu(false)} align="end" />}
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => {
            if (e.target.files?.length && boardId) void uploadManager.enqueueFiles(Array.from(e.target.files), boardId);
            e.target.value = '';
          }} />
          <input ref={dirInput} type="file" hidden {...({ webkitdirectory: '', directory: '' } as Record<string, string>)} onChange={(e) => {
            if (e.target.files?.length && boardId) void uploadManager.enqueueFiles(Array.from(e.target.files), boardId, { useRelativePaths: true });
            e.target.value = '';
          }} />
        </>
      )}
      <NotificationBell /> {/* release3/A */}
      <button ref={userBtn} type="button" aria-label="Account menu" aria-haspopup="menu" aria-expanded={userMenu} onClick={() => setUserMenu(true)} className={cx('rounded-full', userMenu && 'ring-2 ring-accent')}>
        <Avatar actor={me?.user} size={28} />
      </button>
      {userMenu && <Menu anchor={userBtn.current} items={userItems} onClose={() => setUserMenu(false)} align="end" />}
      <span className="sr-only">{isMac() ? 'Cmd' : 'Ctrl'}+K opens the command palette</span>
    </header>
  );
}
